import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError, formatDate, formatDateLocal, formatCurrency, calcLogisticsU, calcMoneyCostE, calcAdjustedPrice, calcAdjusted1cErp } from '@/lib/api';
import { getI18n } from '@/lib/i18n';
import { useWebSocket } from '@/hooks/useWebSocket';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import TenderAttachments from '@/components/TenderAttachments';
import ApplicationAttachments from '@/components/ApplicationAttachments';
import { ChatButton } from '@/components/ChatButton';
import { toast } from 'sonner';
import {
  Search, Package, Clock, TrendingDown, ChevronDown, ChevronUp,
  Truck, DollarSign, Award, Send, Wifi, WifiOff, History
} from 'lucide-react';

const PHASE_STYLES = {
  draft: 'bg-slate-100 text-slate-600',
  published: 'bg-blue-50 text-blue-700',
  waiting: 'bg-slate-100 text-slate-600',
  collecting: 'bg-amber-50 text-amber-700',
  waiting_bids: 'bg-slate-100 text-slate-600',
  bidding: 'bg-emerald-50 text-emerald-700',
  completed: 'bg-slate-100 text-slate-700',
};

function MyBidHistorySection({ tender, history, setHistory, t }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (!open && history === null) {
      setLoading(true);
      try {
        const { data } = await api.get(`/tenders/${tender.id}/my-bid-history`);
        setHistory(data || []);
      } catch { setHistory([]); }
      finally { setLoading(false); }
    }
    setOpen(!open);
  };

  return (
    <div className="border border-slate-200 rounded-sm bg-slate-50/50" data-testid={`history-${tender.id}`}>
      <button onClick={toggle} data-testid={`history-toggle-${tender.id}`}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-slate-100/60 transition">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-[#0046AD]" />
          <span className="text-sm font-semibold text-slate-900">{t('history.title')}</span>
          {history !== null && (
            <Badge variant="outline" className="text-[10px] rounded-sm">{history.length}</Badge>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-slate-200 pt-3">
          {loading ? (
            <div className="text-sm text-slate-400 py-2">{t('history.loading')}</div>
          ) : !history || history.length === 0 ? (
            <div className="text-sm text-slate-400 py-2">{t('history.empty')}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-white">
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t('history.col.time')}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">{t('history.col.price')}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">{t('history.col.prepay')}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">{t('history.col.delay')}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">{t('history.col.adjusted')}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t('history.col.action')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((h, idx) => (
                  <TableRow key={idx} className="border-b border-slate-100">
                    <TableCell className="text-xs text-slate-600 tabular-nums">
                      {new Date(h.created_at).toLocaleString('ru-RU', { hour12: false })}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-semibold">
                      {(h.price_per_ton || 0).toFixed(2)} {tender.currency}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs text-slate-600">
                      {h.prepay_percent || 0}% / {h.prepay_days || 0} {t('history.days_short')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs text-slate-600">
                      {h.delay_percent || 0}% / {h.delay_days || 0} {t('history.days_short')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-bold text-[#0046AD]">
                      {(h.adjusted_price || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] rounded-sm ${h.action === 'create' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                        {h.action === 'create' ? t('history.action.create') : t('history.action.update')}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </div>
  );
}

function TenderBidCard({ tender, onBidSubmitted, userEmail, userTz, t }) {
  // Для завершённых/отменённых — карточка по умолчанию свёрнута, без формы ставок.
  const isCompleted = tender.phase === 'completed' || tender.status === 'cancelled';
  const [expanded, setExpanded] = useState(!isCompleted);
  const [showRankings, setShowRankings] = useState(false);
  const [rankings, setRankings] = useState([]);
  const [myBid, setMyBid] = useState(null);
  const [history, setHistory] = useState(null);
  const [myApplication, setMyApplication] = useState(null);
  const [applicationLoading, setApplicationLoading] = useState(false);
  const [applicationOpen, setApplicationOpen] = useState(false);
  const [appForm, setAppForm] = useState({
    price_per_ton: '', weight_in_container: '',
    prepay_percent: '', prepay_days: '',
    delay_percent: '', delay_days: '', comment: '',
  });
  const [liveBidEnd, setLiveBidEnd] = useState(tender.bid_end);
  const [form, setForm] = useState({
    price_per_ton: '', weight_in_container: '',
    prepay_percent: '30', prepay_days: '10',
    delay_percent: '70', delay_days: '10',
    comment: '',
  });
  const [loading, setLoading] = useState(false);

  // WebSocket для real-time обновлений
  const { lastMessage, isConnected } = useWebSocket(tender.id);

  // Обработка WebSocket сообщений
  useEffect(() => {
    if (lastMessage?.type === 'bid_update' && lastMessage.tender_id === tender.id) {
      const hideOthers = !!(lastMessage.hide_participants ?? tender.hide_participants);
      let updatedRankings = (lastMessage.rankings || []).map((r, idx) => ({
        ...r,
        alias: `${t('supplier.alias')} ${String.fromCharCode(65 + idx)}`,
        is_you: r.user_email === userEmail,
        price_per_ton: r.user_email === userEmail ? r.price_per_ton : null,
        indicator_U: r.user_email === userEmail ? r.indicator_U : null,
        indicator_E: r.user_email === userEmail ? r.indicator_E : null,
      }));
      // «Скрыть участников» — в живых обновлениях тоже показываем только свою
      // строку, без букв-псевдонимов, чтобы не «протекало» число конкурентов.
      if (hideOthers) {
        updatedRankings = updatedRankings
          .filter(r => r.is_you)
          .map(r => ({ ...r, alias: '' }));
      }
      setRankings(updatedRankings);
      if (lastMessage.bid_end) {
        setLiveBidEnd(lastMessage.bid_end);
      }
    }
  }, [lastMessage, tender.id, userEmail, tender.hide_participants, t]);

  // Загрузить мою заявку на участие, если тендер требует её
  useEffect(() => {
    if (!tender.requires_application) return;
    api.get(`/tenders/${tender.id}/applications`).then(({ data }) => {
      const a = (data || [])[0] || null;
      setMyApplication(a);
      if (a) {
        setAppForm(prev => ({
          ...prev,
          price_per_ton: a.bid_price_per_ton != null ? String(a.bid_price_per_ton) : '',
          weight_in_container: a.bid_weight_in_container != null ? String(a.bid_weight_in_container) : '',
          prepay_percent: a.bid_prepay_percent != null ? String(a.bid_prepay_percent) : '',
          prepay_days: a.bid_prepay_days != null ? String(a.bid_prepay_days) : '',
          delay_percent: a.bid_delay_percent != null ? String(a.bid_delay_percent) : '',
          delay_days: a.bid_delay_days != null ? String(a.bid_delay_days) : '',
          comment: a.bid_comment || '',
        }));
      }
    }).catch(() => {});
  }, [tender.id, tender.requires_application]);

  const submitApplication = async () => {
    setApplicationLoading(true);
    try {
      // Собираем только заполненные поля — все опциональны.
      const payload = {};
      const numFields = ['price_per_ton', 'weight_in_container', 'prepay_percent', 'prepay_days', 'delay_percent', 'delay_days'];
      for (const f of numFields) {
        if (appForm[f] !== '' && appForm[f] != null) payload[f] = parseFloat(appForm[f]);
      }
      if (appForm.comment) payload.comment = appForm.comment;

      const { data } = await api.post(`/tenders/${tender.id}/applications`, payload);
      setMyApplication(data);
      setApplicationOpen(false);
      toast.success(t('app.submitted_toast'));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setApplicationLoading(false); }
  };

  const updateAppField = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setAppForm(p => ({ ...p, [k]: v }));
  };

  const isLogisticsTender = (tender.tender_direction || tender.direction || '').toLowerCase() === 'logistics';
  const isImportTender = !isLogisticsTender && !!tender.is_import;

  // Валидация формы предварительной заявки.
  // Кнопка «Подать заявку» disabled пока есть пустые поля или логические ошибки.
  // Поля: price_per_ton + (для не-логистических: weight, prepay%, prepay_d, delay%, delay_d).
  const applicationValidation = useMemo(() => {
    const errs = {};
    const isImp = !!tender.is_import && !isLogisticsTender;
    const num = (v) => {
      if (v === '' || v == null) return null;
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : NaN;
    };
    const price = num(appForm.price_per_ton);
    if (price === null) errs.price_per_ton = t('app.err.required');
    else if (Number.isNaN(price) || price <= 0) errs.price_per_ton = t('app.err.positive');

    if (!isLogisticsTender) {
      const weight = num(appForm.weight_in_container);
      // Для импортного лота W не участвует в формуле, но всё равно полезен для отчётности.
      // Делаем обязательным только для legacy не-импортных.
      if (!isImp) {
        if (weight === null) errs.weight_in_container = t('app.err.required');
        else if (Number.isNaN(weight) || weight <= 0) errs.weight_in_container = t('app.err.positive');
      } else if (weight !== null && (Number.isNaN(weight) || weight <= 0)) {
        errs.weight_in_container = t('app.err.positive');
      }
      const pp = num(appForm.prepay_percent);
      const pd = num(appForm.prepay_days);
      const dp = num(appForm.delay_percent);
      const dd = num(appForm.delay_days);
      [['prepay_percent', pp], ['prepay_days', pd], ['delay_percent', dp], ['delay_days', dd]].forEach(([k, v]) => {
        if (v === null) errs[k] = t('app.err.required');
        else if (Number.isNaN(v) || v < 0) errs[k] = t('app.err.non_negative');
      });
      if (pp !== null && !Number.isNaN(pp) && (pp < 0 || pp > 100)) errs.prepay_percent = t('app.err.range_0_100');
      if (dp !== null && !Number.isNaN(dp) && (dp < 0 || dp > 100)) errs.delay_percent = t('app.err.range_0_100');
      if (pp !== null && dp !== null && !Number.isNaN(pp) && !Number.isNaN(dp)) {
        const sum = pp + dp;
        if (Math.abs(sum - 100) > 0.01) {
          const m = t('app.err.sum_100').replace('{val}', sum.toFixed(1));
          errs.prepay_percent = errs.prepay_percent || m;
          errs.delay_percent = errs.delay_percent || m;
        }
      }
    }
    return { errors: errs, valid: Object.keys(errs).length === 0 };
  }, [appForm, isLogisticsTender, tender.is_import, t]);

  const appErrors = applicationValidation.errors;
  const appIsValid = applicationValidation.valid;

  // Загрузить текущую ставку при монтировании
  useEffect(() => {
    api.get(`/tenders/${tender.id}/bids`).then(({ data }) => {
      if (data.length > 0) {
        setMyBid(data[0]);
        setForm({
          price_per_ton: String(data[0].price_per_ton),
          weight_in_container: String(data[0].weight_in_container),
          prepay_percent: String(data[0].prepay_percent),
          prepay_days: String(data[0].prepay_days),
          delay_percent: String(data[0].delay_percent),
          delay_days: String(data[0].delay_days),
          comment: data[0].comment || '',
        });
      }
    }).catch(() => {});
  }, [tender.id]);

  // Расчёт индикаторов в реальном времени
  const price = parseFloat(form.price_per_ton) || 0;
  const weight = parseFloat(form.weight_in_container) || 0;
  const prepayPct = parseFloat(form.prepay_percent) || 0;
  const prepayDays = parseInt(form.prepay_days) || 0;
  const delayPct = parseFloat(form.delay_percent) || 0;
  const delayDays = parseInt(form.delay_days) || 0;

  // Для импортного покупательского лота используем 1C ERP формулу (зеркало backend math_engine.py).
  let U = 0, E = 0, adjusted = price;
  if (isImportTender) {
    // Live-расчёт 1C ERP для импортного лота. Bank и Customs/ед. в UI скрыты
    // (см. ТЗ — приватные компоненты покупателя), но в общую Adjusted они входят.
    const r = calcAdjusted1cErp(tender, userEmail, price, prepayPct, prepayDays, delayPct, delayDays, weight);
    U = r.U; E = r.E; adjusted = r.adjusted;
  } else if (!isLogisticsTender) {
    U = calcLogisticsU(tender.volume, price, weight, tender.logistics_cost_per_container);
    E = calcMoneyCostE(price, prepayPct, prepayDays, delayPct, delayDays, tender.money_cost_day);
    adjusted = calcAdjustedPrice(price, U, E);
  }

  const percentSum = prepayPct + delayPct;
  const isValidPercent = isLogisticsTender ? true : Math.abs(percentSum - 100) < 0.01;
  const isValidWeight = isLogisticsTender ? true : (weight > 0 && weight <= (tender.max_container_weight || 100));

  // Локальный пересчёт фазы по текущему времени (обновляется каждые 15 сек)
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(timer);
  }, []);

  const computePhase = useCallback(() => {
    const parseMsk = (s) => {
      if (!s) return null;
      // Если нет timezone, считаем МСК (+03:00)
      if (!s.includes('+') && !s.includes('Z') && s.length > 10) {
        return new Date(s + '+03:00').getTime();
      }
      return new Date(s).getTime();
    };
    const appStart = parseMsk(tender.app_start);
    const appEnd = parseMsk(tender.app_end);
    const bidStart = parseMsk(tender.bid_start);
    const bidEnd = parseMsk(liveBidEnd);
    if (!appStart || tender.status === 'draft') return tender.phase || 'draft';
    if (now < appStart) return 'waiting';
    if (now <= appEnd) return 'collecting';
    if (now < bidStart) return 'waiting_bids';
    if (now <= bidEnd) return 'bidding';
    return 'completed';
  }, [tender, liveBidEnd, now]);

  const phase = computePhase();
  // Ставку можно подать ТОЛЬКО во время торгов (`bidding`).
  // На этапе `collecting` доступна только подача заявки на участие (через apply-btn).
  const isBiddingPhase = phase === 'bidding';
  const canSubmit = price > 0 && isValidWeight && isValidPercent && isBiddingPhase;

  // Сообщение по фазе
  const phaseMessage = {
    waiting: { text: `${t('phase.msg.waiting')} ${formatDateLocal(tender.app_start, userTz)}. ${t('phase.msg.waiting_hint')}`, color: 'bg-amber-50 text-amber-700 border-amber-200' },
    waiting_bids: { text: `${t('phase.msg.waiting_bids')} ${formatDateLocal(tender.bid_start, userTz)}. ${t('phase.msg.waiting_bids_hint')}`, color: 'bg-blue-50 text-blue-700 border-blue-200' },
    completed: { text: t('phase.msg.completed'), color: 'bg-slate-100 text-slate-600 border-slate-200' },
  }[phase];

  const handleSubmit = async (opts = {}) => {
    const isAlt = !!opts.isAlternative;
    if (isAlt && !tender.allow_alternative_bids) return;
    let altName = opts.optionName;
    if (isAlt && !altName) {
      altName = window.prompt(t('bid.alt_option_prompt'), 'ЖД доставка');
      if (!altName || !altName.trim()) return;
    }
    setLoading(true);
    try {
      await api.post('/bids', {
        tender_id: tender.id,
        price_per_ton: price,
        weight_in_container: weight,
        prepay_percent: prepayPct,
        prepay_days: prepayDays,
        delay_percent: delayPct,
        delay_days: delayDays,
        comment: form.comment,
        is_alternative: isAlt,
        option_name: altName || 'Основное предложение',
      });
      toast.success(isAlt ? t('bid.alt_added_toast') : (myBid ? t('bid.updated_toast') : t('bid.submitted_toast')));
      if (!isAlt) setMyBid({ ...form });
      onBidSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const loadRankings = async () => {
    try {
      const { data } = await api.get(`/tenders/${tender.id}/rankings`);
      setRankings(data);
      setShowRankings(true);
    } catch { setRankings([]); }
  };

  const phaseLabel = t(`phase.${phase}`);
  const phaseCls = PHASE_STYLES[phase] || PHASE_STYLES.published;
  const update = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm hover-lift" data-testid={`tender-card-${tender.id}`}>
      {/* Header */}
      <button
        className="w-full p-4 flex items-start justify-between text-left"
        onClick={() => setExpanded(!expanded)}
        data-testid={`tender-toggle-${tender.id}`}
      >
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-4 h-4 text-[#0046AD]" />
            <h3 className="font-heading text-base font-semibold text-slate-900">{tender.lot_name}</h3>
            <Badge variant="outline" className={`phase-badge ${phaseCls}`}>{phaseLabel}</Badge>
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span>{tender.product_name}</span>
            <span>{tender.volume} {tender.volume_unit || t('unit.ton_short')}</span>
            <span>{tender.currency}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t('part.until')}: {formatDateLocal(liveBidEnd, userTz)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {expanded && (
            <span className={`flex items-center gap-1 text-xs ${isConnected ? 'text-emerald-600' : 'text-slate-400'}`} data-testid={`ws-status-${tender.id}`}>
              {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {isConnected ? 'LIVE' : ''}
            </span>
          )}
          <span onClick={(e) => e.stopPropagation()} className="inline-flex">
            <ChatButton tenderId={tender.id} supplierEmail={userEmail} labelKey="chat.btn.with_organizer" />
          </span>
          {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          <Separator className="mb-4" />

          {/* Tender Info */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 text-sm">
            <div className="bg-slate-50 p-2.5 rounded-sm">
              <span className="text-xs text-slate-500 block">{t('info.expected_price')}</span>
              <span className="font-semibold text-slate-900">{formatCurrency(tender.expected_price, tender.currency)}</span>
            </div>
            {!isLogisticsTender && (
              <div className="bg-slate-50 p-2.5 rounded-sm">
                <span className="text-xs text-slate-500 block">{t('info.logistics')}</span>
                <span className="font-semibold text-slate-900">{formatCurrency(tender.logistics_cost_per_container, tender.currency)}</span>
              </div>
            )}
            <div className="bg-slate-50 p-2.5 rounded-sm">
              <span className="text-xs text-slate-500 block">{t('info.max_weight')}</span>
              <span className="font-semibold text-slate-900">{tender.max_container_weight} {t('unit.ton_short')}</span>
            </div>
            {!isLogisticsTender && (
              <div className="bg-slate-50 p-2.5 rounded-sm">
                <span className="text-xs text-slate-500 block">{t('info.money_coeff')}</span>
                <span className="font-semibold text-slate-900">{tender.money_cost_day}%</span>
              </div>
            )}
          </div>

          {/* Расписание этапов */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 text-xs">
            <div className={`p-2.5 rounded-sm border ${phase === 'collecting' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
              <span className="text-slate-500 block mb-0.5">{t('info.app_start')}</span>
              <span className="font-medium text-slate-900">{formatDateLocal(tender.app_start, userTz)}</span>
            </div>
            <div className={`p-2.5 rounded-sm border ${phase === 'collecting' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
              <span className="text-slate-500 block mb-0.5">{t('info.app_end')}</span>
              <span className="font-medium text-slate-900">{formatDateLocal(tender.app_end, userTz)}</span>
            </div>
            <div className={`p-2.5 rounded-sm border ${phase === 'bidding' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}>
              <span className="text-slate-500 block mb-0.5">{t('info.bid_start')}</span>
              <span className="font-medium text-slate-900">{formatDateLocal(tender.bid_start, userTz)}</span>
            </div>
            <div className={`p-2.5 rounded-sm border ${phase === 'bidding' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}>
              <span className="text-slate-500 block mb-0.5">{t('info.bid_end')}</span>
              <span className="font-medium text-slate-900">{formatDateLocal(liveBidEnd, userTz)}</span>
            </div>
          </div>

          {/* Описание лота */}
          {tender.description && (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mb-4">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">{t('info.lot_description')}</span>
              <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{tender.description}</p>
            </div>
          )}

          {/* Файлы тендера (просмотр + скачивание) */}
          <TenderAttachments tenderId={tender.id} canEdit={false} />

          {/* Заявка на участие (этап collecting/waiting) */}
          {tender.requires_application && (phase === 'waiting' || phase === 'collecting') && (
            <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`my-application-${tender.id}`}>
              <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-sm">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-amber-900 mb-0.5">{t('app.title')}</div>
                    {!myApplication ? (
                      <div className="text-xs text-amber-700">{t('app.hint_pending')}</div>
                    ) : myApplication.status === 'pending' ? (
                      <div className="text-xs text-amber-700">{t('app.status_pending')}</div>
                    ) : myApplication.status === 'accepted' ? (
                      <div className="text-xs text-emerald-700">{t('app.status_accepted')}</div>
                    ) : (
                      <div className="text-xs text-rose-700">
                        {t('app.status_rejected')}
                        {myApplication.reason && (
                          <div className="mt-1 text-[11px] text-rose-700/90 italic">
                            {t('app.rejection_reason')}: {myApplication.reason}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {(!myApplication || myApplication.status === 'rejected') && (
                    <Button onClick={() => setApplicationOpen(o => !o)} size="sm"
                      data-testid={`apply-toggle-${tender.id}`}
                      variant="outline"
                      className="rounded-sm shrink-0 text-xs">
                      {applicationOpen ? t('app.hide_form') : (myApplication?.status === 'rejected' ? t('app.resubmit_show_form') : t('app.show_form'))}
                    </Button>
                  )}
                </div>

                {/* Форма с опциональными первичными условиями ставки */}
                {(!myApplication || myApplication.status === 'rejected') && applicationOpen && (
                  <div className="mt-3 p-3 bg-white border border-amber-300 rounded-sm space-y-2.5">
                    <p className="text-[11px] text-slate-500">
                      {myApplication?.status === 'rejected' ? t('app.resubmit_hint') : t('app.bid_optional_hint')}
                    </p>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[11px] text-slate-600">{t('bid.price_ton')} <span className="text-slate-400">{tender.currency}/{tender.volume_unit || t('unit.ton_short')}</span></Label>
                        <Input type="number" step="0.01" value={appForm.price_per_ton} onChange={updateAppField('price_per_ton')}
                          placeholder="—" data-testid={`app-price-${tender.id}`}
                          className={`border-slate-300 rounded-sm tabular-nums h-8 text-sm ${appErrors.price_per_ton ? 'border-rose-400 bg-rose-50' : ''}`} />
                        {appErrors.price_per_ton && <p className="text-[10px] text-rose-600">{appErrors.price_per_ton}</p>}
                      </div>
                      {!isLogisticsTender && (<>
                      <div className="space-y-1">
                        <Label className="text-[11px] text-slate-600">{t('bid.weight')}{isImportTender && <span className="text-slate-400"> ({t('app.optional_for_import')})</span>}</Label>
                        <Input type="number" step="0.01" value={appForm.weight_in_container} onChange={updateAppField('weight_in_container')}
                          placeholder="—" data-testid={`app-weight-${tender.id}`}
                          className={`border-slate-300 rounded-sm tabular-nums h-8 text-sm ${appErrors.weight_in_container ? 'border-rose-400 bg-rose-50' : ''}`} />
                        {appErrors.weight_in_container && <p className="text-[10px] text-rose-600">{appErrors.weight_in_container}</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px] text-slate-600">{t('bid.prepay_pct')}</Label>
                        <Input type="number" step="1" value={appForm.prepay_percent} onChange={updateAppField('prepay_percent')}
                          placeholder="—" data-testid={`app-prepay-pct-${tender.id}`}
                          className={`border-slate-300 rounded-sm tabular-nums h-8 text-sm ${appErrors.prepay_percent ? 'border-rose-400 bg-rose-50' : ''}`} />
                        {appErrors.prepay_percent && <p className="text-[10px] text-rose-600">{appErrors.prepay_percent}</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px] text-slate-600">{t('bid.prepay_days')}</Label>
                        <Input type="number" step="1" value={appForm.prepay_days} onChange={updateAppField('prepay_days')}
                          placeholder="—" data-testid={`app-prepay-days-${tender.id}`}
                          className={`border-slate-300 rounded-sm tabular-nums h-8 text-sm ${appErrors.prepay_days ? 'border-rose-400 bg-rose-50' : ''}`} />
                        {appErrors.prepay_days && <p className="text-[10px] text-rose-600">{appErrors.prepay_days}</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px] text-slate-600">{t('bid.delay_pct')}</Label>
                        <Input type="number" step="1" value={appForm.delay_percent} onChange={updateAppField('delay_percent')}
                          placeholder="—" data-testid={`app-delay-pct-${tender.id}`}
                          className={`border-slate-300 rounded-sm tabular-nums h-8 text-sm ${appErrors.delay_percent ? 'border-rose-400 bg-rose-50' : ''}`} />
                        {appErrors.delay_percent && <p className="text-[10px] text-rose-600">{appErrors.delay_percent}</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px] text-slate-600">{t('bid.delay_days')}</Label>
                        <Input type="number" step="1" value={appForm.delay_days} onChange={updateAppField('delay_days')}
                          placeholder="—" data-testid={`app-delay-days-${tender.id}`}
                          className={`border-slate-300 rounded-sm tabular-nums h-8 text-sm ${appErrors.delay_days ? 'border-rose-400 bg-rose-50' : ''}`} />
                        {appErrors.delay_days && <p className="text-[10px] text-rose-600">{appErrors.delay_days}</p>}
                      </div>
                      </>)}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] text-slate-600">{t('bid.comment')}</Label>
                      <Input value={appForm.comment} onChange={updateAppField('comment')}
                        placeholder="—" data-testid={`app-comment-${tender.id}`}
                        className="border-slate-300 rounded-sm h-8 text-sm" />
                    </div>
                  </div>
                )}

                {(!myApplication || myApplication.status === 'rejected') && (
                  <div className="flex items-center justify-end gap-2 mt-2">
                    {!appIsValid && (
                      <span className="text-[10px] text-rose-600" data-testid={`apply-validation-hint-${tender.id}`}>
                        {t('app.fix_errors_hint')}
                      </span>
                    )}
                    <Button onClick={submitApplication}
                      disabled={applicationLoading || !appIsValid}
                      title={!appIsValid ? t('app.fix_errors_hint') : ''}
                      size="sm"
                      data-testid={`apply-btn-${tender.id}`}
                      className="bg-[#0046AD] hover:bg-[#003A8C] rounded-sm font-semibold text-xs disabled:bg-slate-300 disabled:cursor-not-allowed">
                      {applicationLoading ? t('app.submitting') : (myApplication?.status === 'rejected' ? t('app.resubmit_btn') : t('app.submit_btn'))}
                    </Button>
                  </div>
                )}

                {myApplication?.id && (
                  <ApplicationAttachments
                    applicationId={myApplication.id}
                    canEdit={['pending', 'rejected'].includes(myApplication.status)}
                  />
                )}
              </div>
            </div>
          )}

          {/* Phase Status Message */}
          {phaseMessage && !isCompleted && (
            <div className={`border rounded-sm px-4 py-3 mb-4 mt-4 text-sm ${phaseMessage.color}`} data-testid={`phase-message-${tender.id}`}>
              <Clock className="w-4 h-4 inline mr-1.5" />
              {phaseMessage.text}
            </div>
          )}

          {/* Форма ставок — только для активных (не завершённых) тендеров */}
          {!isCompleted && (
          <div className="border border-slate-200 rounded-sm p-4 mb-4 mt-4" data-testid={`bid-form-${tender.id}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-900">
                {myBid ? t('bid.update_title') : t('bid.submit_title')}
              </h4>
              {myBid && (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                  {t('bid.submitted')}
                </Badge>
              )}
            </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">{t('bid.price_ton')} <span className="text-slate-400">— {tender.currency}/{tender.volume_unit || t('unit.ton_short')}</span></Label>
                  <Input type="number" step="0.01" value={form.price_per_ton} onChange={update('price_per_ton')}
                    placeholder="0.00" data-testid={`bid-price-${tender.id}`}
                    className="border-slate-300 rounded-sm tabular-nums" />
                </div>
                {!isLogisticsTender && (<>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">{t('bid.weight')}</Label>
                  <Input type="number" step="0.01" value={form.weight_in_container} onChange={update('weight_in_container')}
                    placeholder="0.00" data-testid={`bid-weight-${tender.id}`}
                    className="border-slate-300 rounded-sm tabular-nums" />
                  {weight > 0 && !isValidWeight && (
                    <p className="text-xs text-rose-600">{t('bid.max_weight')}: {tender.max_container_weight} {t('unit.ton_short')}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">{t('bid.prepay_pct')}</Label>
                  <Input type="number" step="0.01" value={form.prepay_percent} onChange={update('prepay_percent')}
                    data-testid={`bid-prepay-pct-${tender.id}`} className="border-slate-300 rounded-sm tabular-nums" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">{t('bid.prepay_days')}</Label>
                  <Input type="number" value={form.prepay_days} onChange={update('prepay_days')}
                    data-testid={`bid-prepay-days-${tender.id}`} className="border-slate-300 rounded-sm tabular-nums" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">{t('bid.delay_pct')}</Label>
                  <Input type="number" step="0.01" value={form.delay_percent} onChange={update('delay_percent')}
                    data-testid={`bid-delay-pct-${tender.id}`} className="border-slate-300 rounded-sm tabular-nums" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">{t('bid.delay_days')}</Label>
                  <Input type="number" value={form.delay_days} onChange={update('delay_days')}
                    data-testid={`bid-delay-days-${tender.id}`} className="border-slate-300 rounded-sm tabular-nums" />
                </div>
                </>)}
              </div>

              {/* Comment */}
              <div className="mb-3">
                <Label className="text-xs font-medium text-slate-600">{t('bid.comment')}</Label>
                <Textarea value={form.comment} onChange={e => setForm(p => ({...p, comment: e.target.value}))}
                  placeholder={t('bid.comment.placeholder')} rows={2}
                  data-testid={`bid-comment-${tender.id}`}
                  className="border-slate-300 rounded-sm text-sm mt-1" />
              </div>

              {!isValidPercent && (
                <p className="text-xs text-rose-600 mb-2">{t('bid.percent_sum')}: {percentSum.toFixed(1)}% ({t('bid.percent_must')})</p>
              )}

              {/* Real-time Indicators */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
                {!isLogisticsTender && (
                  <>
                    <div className="flex items-center gap-1.5">
                      <Truck className="w-3.5 h-3.5 text-indigo-600" />
                      <span className="text-xs text-slate-500">{t('bid.indicator_u')}:</span>
                      <span className="indicator-badge bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid={`indicator-u-${tender.id}`}>
                        {U.toFixed(2)} {tender.currency}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <DollarSign className="w-3.5 h-3.5 text-cyan-600" />
                      <span className="text-xs text-slate-500">{t('bid.indicator_e')}:</span>
                      <span className="indicator-badge bg-cyan-50 text-cyan-700 border border-cyan-200" data-testid={`indicator-e-${tender.id}`}>
                        {E.toFixed(2)} {tender.currency}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex items-center gap-1.5">
                  <TrendingDown className="w-3.5 h-3.5 text-slate-600" />
                  <span className="text-xs text-slate-500">
                    {isLogisticsTender ? t('bid.price') : t('bid.adjusted_label')}:
                  </span>
                  <span className="text-sm font-bold text-slate-900 tabular-nums" data-testid={`adjusted-price-${tender.id}`}>
                    {adjusted.toFixed(2)} {tender.currency}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => handleSubmit()} disabled={!canSubmit || loading} data-testid={`submit-bid-${tender.id}`}
                  className="bg-[#0046AD] hover:bg-[#003A8C] rounded-sm font-semibold text-sm">
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {loading ? t('bid.sending') : myBid ? t('bid.update_btn') : t('bid.submit_btn')}
                </Button>
                {tender.allow_alternative_bids && (
                  <Button
                    onClick={() => handleSubmit({ isAlternative: true })}
                    disabled={!canSubmit || loading}
                    variant="outline"
                    data-testid={`submit-alt-bid-${tender.id}`}
                    className="rounded-sm font-semibold text-sm border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                  >
                    + {t('bid.add_alt_btn')}
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={loadRankings} data-testid={`view-rankings-${tender.id}`}
                  className="rounded-sm text-sm">
                  <Award className="w-3.5 h-3.5 mr-1.5" /> {t('bid.ranking')}
                </Button>
              </div>
              {!isBiddingPhase && phase !== 'completed' && (
                <p className="text-xs text-amber-600 mt-2">{t('bid.phase_inactive')}</p>
              )}
            </div>
          )}

          {/* История моих ставок (только для завершённых / отменённых тендеров) */}
          {isCompleted && (
            <MyBidHistorySection tender={tender} history={history} setHistory={setHistory} t={t} />
          )}

          {/* Rankings */}
          {showRankings && rankings.length > 0 && (() => {
            // Для тендеров направления «закупка масла» участник видит только 2 первых места.
            const isOilTender = tender.tender_direction === 'purchase_oil';
            const isHiddenMode = !!tender.hide_participants;
            const visibleRankings = isOilTender ? rankings.filter(r => r.rank <= 2) : rankings;
            return (
            <div className="border border-slate-200 rounded-sm" data-testid={`rankings-${tender.id}`}>
              <div className="p-3 border-b border-slate-200 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-900">
                  {t('rank.title')}
                  {isHiddenMode && (
                    <span className="ml-2 text-xs text-slate-500 font-normal">
                      · только ваша ставка (участники скрыты организатором)
                    </span>
                  )}
                  {!isHiddenMode && isOilTender && (
                    <span className="ml-2 text-xs text-slate-500 font-normal">
                      · только ТОП-2
                    </span>
                  )}
                </h4>
                <Button variant="ghost" size="sm" onClick={() => setShowRankings(false)} className="text-xs text-slate-400">{t('rank.hide')}</Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-16">{t('rank.place')}</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t('rank.participant')}</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">{t('rank.adjusted_price')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRankings.map((r, idx) => (
                    <TableRow key={idx} className={`border-b border-slate-100 ${r.is_you ? 'bg-blue-50/50' : 'hover:bg-slate-50/50'}`}>
                      <TableCell>
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${r.rank === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                          {r.rank}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        {r.alias} {r.is_you && <Badge className="ml-2 bg-[#0046AD] text-white text-[10px]">{t('rank.you')}</Badge>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm font-bold text-slate-900">
                        {r.adjusted_price?.toFixed(2)} {tender.currency}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default function ParticipantDashboard() {
  const { user } = useAuth();
  const { t } = getI18n(user);
  const [tenders, setTenders] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const loadTenders = useCallback(async () => {
    try {
      const { data } = await api.get('/tenders', { params: { search } });
      setTenders(data);
    } catch {}
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { loadTenders(); }, [loadTenders]);

  // Live-обновление: WS-уведомление об удалении тендера → убираем из списка.
  useEffect(() => {
    const handler = (e) => {
      const tid = e.detail?.tender_id;
      if (!tid) return;
      setTenders(prev => prev.filter(t => t.id !== tid));
    };
    window.addEventListener('tender:removed', handler);
    return () => window.removeEventListener('tender:removed', handler);
  }, []);

  return (
    <div className="p-3" data-testid="participant-dashboard">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{t('part.title')}</h1>
        <p className="text-sm text-slate-500 mt-1">{t('part.subtitle')}</p>
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder={t('part.search')} value={search} onChange={e => setSearch(e.target.value)}
          className="pl-9 border-slate-300 rounded-sm" data-testid="search-participant-tenders" />
      </div>

      {/* Tender Cards */}
      <div className="space-y-3">
        {tenders.map(tender => (
          <TenderBidCard key={tender.id} tender={tender} onBidSubmitted={loadTenders} userEmail={user?.email} userTz={user?.timezone} t={t} />
        ))}
        {!loading && tenders.length === 0 && (
          <div className="text-center py-16 text-slate-400">
            <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-lg">{t('part.no_tenders')}</p>
            <p className="text-sm mt-1">{t('part.no_tenders_hint')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
